
/**
 * Utilities for refreshing UI and cache
 */

/**
 * Create a visual effect that indicates the UI is being refreshed
 */
export function pulseUIRefresh(): void {
  // Create a temporary overlay for the pulse effect
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = '9999';
  overlay.style.opacity = '0';
  overlay.style.transition = 'opacity 0.3s ease-in-out';
  document.body.appendChild(overlay);

  // Flash the overlay
  setTimeout(() => {
    overlay.style.opacity = '0.2';
    setTimeout(() => {
      overlay.style.opacity = '0';
      setTimeout(() => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      }, 300);
    }, 150);
  }, 10);
}

/**
 * Add custom CSS to force layout recalculation
 */
export function forceLayoutRecalculation(): void {
  const dummyElement = document.createElement('div');
  dummyElement.id = 'force-recalc';
  dummyElement.style.position = 'fixed';
  dummyElement.style.width = '1px';
  dummyElement.style.height = '1px';
  dummyElement.style.opacity = '0.01';
  dummyElement.style.pointerEvents = 'none';
  dummyElement.style.zIndex = '-1000';
  
  // Get a timestamp and version ID (safely)
  const timestamp = Date.now();
  const versionId = typeof window !== 'undefined' && window.__VERSION_ID__ ? 
    window.__VERSION_ID__ : timestamp.toString();
  
  document.body.appendChild(dummyElement);
  const style = document.createElement('style');
  style.textContent = `
    #force-recalc {
      --refresh-time: ${timestamp};
      --force-refresh: ${versionId};
    }
    
    body {
      --refresh-indicator: ${timestamp};
    }
    
    html {
      --refresh-state: ${timestamp % 1000};
    }
  `;
  document.head.appendChild(style);
  
  // Force reflow by accessing computed style
  window.getComputedStyle(dummyElement).getPropertyValue('opacity');
  
  // Remove after a short delay
  setTimeout(() => {
    if (dummyElement.parentNode) {
      dummyElement.parentNode.removeChild(dummyElement);
    }
    if (style.parentNode) {
      style.parentNode.removeChild(style);
    }
  }, 2000);
}

/**
 * Clear any AI Readiness references from DOM and storage
 */
export function clearAIReadinessReferences(): void {
  // Find and remove any AI Readiness elements that might be lingering
  setTimeout(() => {
    try {
      // 1. First clean up localStorage and sessionStorage
      // Local Storage cleanup
      Object.keys(localStorage).forEach(key => {
        if (key.toLowerCase().includes('ai') || key.toLowerCase().includes('readiness')) {
          console.log('Removing localStorage item:', key);
          localStorage.removeItem(key);
        } else {
          // Check content of items
          try {
            const content = localStorage.getItem(key);
            if (content && (
                content.toLowerCase().includes('ai readiness') || 
                content.toLowerCase().includes('aireadiness')
            )) {
              console.log('Removing localStorage item with AI readiness content:', key);
              localStorage.removeItem(key);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      });
      
      // Session Storage cleanup
      Object.keys(sessionStorage).forEach(key => {
        if (key.toLowerCase().includes('ai') || key.toLowerCase().includes('readiness')) {
          console.log('Removing sessionStorage item:', key);
          sessionStorage.removeItem(key);
        } else {
          // Check content of items
          try {
            const content = sessionStorage.getItem(key);
            if (content && (
                content.toLowerCase().includes('ai readiness') || 
                content.toLowerCase().includes('aireadiness')
            )) {
              console.log('Removing sessionStorage item with AI readiness content:', key);
              sessionStorage.removeItem(key);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      });
      
      // 2. Then clean up DOM elements
      // Try to remove any elements with AI readiness text
      const elements = document.querySelectorAll('button, a, div, li, span');
      elements.forEach(el => {
        if (el.textContent && 
            (el.textContent.toLowerCase().includes('ai readiness') || 
             el.textContent.toLowerCase().includes('aireadiness'))) {
          console.log('Found AI readiness element:', el);
          el.remove();
        }
      });
      
      // 3. Try to remove by various selector patterns
      const potentialSelectors = [
        // Data attributes
        '[data-testid="ai-readiness"]',
        '[data-testid="aiReadiness"]',
        '[data-page="ai-readiness"]',
        '[data-section="ai-readiness"]',
        '[data-content*="ai-readiness"]',
        '[data-nav-item*="ai-readiness"]',
        
        // Class names
        '.ai-readiness', 
        '.aiReadiness',
        '.ai-readiness-nav',
        '.ai-readiness-menu-item',
        '.ai_readiness',
        
        // IDs
        '#ai-readiness',
        '#aiReadiness',
        '#ai_readiness_section',
        '#aiReadinessNav',
        
        // Attributes
        '[aria-label*="AI Readiness"]',
        '[title*="AI Readiness"]',
        '[name*="ai-readiness"]',
        '[placeholder*="AI Readiness"]',
        
        // Deep content selectors
        'a:has(span:contains("AI Readiness"))',
        'button:has(span:contains("AI Readiness"))',
        'div:has(span:contains("AI Readiness"))'
      ];
      
      potentialSelectors.forEach(selector => {
        try {
          const selectorElements = document.querySelectorAll(selector);
          selectorElements.forEach(el => {
            console.log('Found AI readiness element by selector:', selector, el);
            el.remove();
          });
        } catch (e) {
          // Ignore selector errors
        }
      });
      
      // 4. Remove any injected scripts that might re-add AI Readiness
      document.querySelectorAll('script').forEach(script => {
        if (script.textContent && 
            (script.textContent.toLowerCase().includes('ai readiness') || 
             script.textContent.toLowerCase().includes('aireadiness'))) {
          console.log('Found script with AI readiness:', script);
          script.remove();
        }
      });
    } catch (e) {
      console.error('Error cleaning up AI readiness elements:', e);
    }
  }, 100);
}

/**
 * Force a full page refresh bypassing cache
 */
export function forceFullRefresh(): void {
  // First pulse the UI to indicate refresh is happening
  pulseUIRefresh();
  
  // Clear AI readiness references
  clearAIReadinessReferences();
  
  // Attempt to clear browser cache for the page
  if ('caches' in window) {
    try {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    } catch (err) {
      console.error('Cache clearing error:', err);
    }
  }
  
  // Force reload the page with cache-busting
  setTimeout(() => {
    const timestamp = Date.now();
    const url = new URL(window.location.href);
    url.searchParams.set('t', timestamp.toString());
    
    // Use a stronger cache-busting approach for the reload
    window.location.href = url.toString();
  }, 300);
}

// No duplicate export here
